/**
 * ============================================================================
 * useDisqualifyMutation — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/suppliers/{id}/disqualify — high-blast-radius action
 *          requiring DUAL signatures + a ≥200-char rationale (SUP-DISQ-001).
 *          The server enforces "distinct signer" and the rationale length
 *          guard — we still pre-validate client-side for fast feedback.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type { SupplierDetail } from './useSupplierDetailQuery';

export interface DisqualifyInput {
  supplierId: string;
  rationale: string;
  signature_bundle_id_a: string;
  signature_bundle_id_b: string;
}

export function useDisqualifyMutation(): UseMutationResult<
  SupplierDetail,
  Error,
  DisqualifyInput
> {
  return useMutation<SupplierDetail, Error, DisqualifyInput>({
    mutationFn: ({ supplierId, ...body }) =>
      apiFetch<SupplierDetail>(
        `/api/v1/suppliers/${encodeURIComponent(supplierId)}/disqualify`,
        {
          method: 'POST',
          body: {
            rationale: body.rationale,
            signature_bundle_ids: [
              body.signature_bundle_id_a,
              body.signature_bundle_id_b,
            ],
          },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
  });
}
